































import { mapActions, mapGetters } from 'vuex';
import { BButtonGroup, BDropdown, BDropdownItemButton } from 'bootstrap-vue';
import gql from 'graphql-tag';
import { keyBy, map } from 'lodash';
import { Column, DataRow } from '@/components/listing/types';
import { downloadFile, filenameFromResponse } from '@/utils';
import { postRequest } from '../../../../utils/client/client';
import { composeFilterQuery, columnsToFields, composeFilterByIdsQuery } from '@/graphql/listing/SorListing.helpers';
import {
  composeOldListingFilterSyntax,
  composeOldFilterByIdsQuery,
  composeOldSortSyntax,
} from '@/graphql/listing/filters/legacyFiltersUtils';
import { ZenFilter } from '../filters/types';
import { downloadAttachments } from '@/components/listing/utils';

export default {
  name: 'ListingActions',
  components: {
    BButtonGroup,
    BDropdown,
    BDropdownItemButton,
  },
  props: {
    totalCount: {
      type: Number,
      default: 0,
    },
    objectType: {
      // In ModelMetaSchema.modelSingular form
      type: String,
      required: true,
    },
    data: {
      type: Array as () => DataRow[],
      default: [],
    },
    columns: {
      type: Array as () => Column[],
      required: true,
    },
    activeFilters: {
      type: Array as () => ZenFilter[],
      default: () => [],
    },
    isBulkActions: {
      type: Boolean,
      default: false,
    },
    selectedObjects: {
      type: Array,
      default: () => [],
    },
    sort: {
      type: Object as () => SortBy,
      required: true,
    },
  },
  computed: {
    ...mapGetters(['csrfToken']),
    showActions(): boolean {
      return this.canCreateObject || this.canExportToCSV || this.canDownloadAttachments;
    },
    loading(): boolean {
      return this.$apollo.queries.objectTypeMeta.loading;
    },
    canDownloadAttachments(): boolean {
      if (this.isBulkActions) return this.selectedObjects.some(el => el.permissions.canAttach);
      return this.objectTypeMeta ? this.objectTypeMeta.isDownloadable : false;
    },
    canExportToCSV(): boolean {
      // TODO : Add also user permission checking
      return this.objectTypeMeta ? this.objectTypeMeta.isExportable : false;
    },
    canCreateObject(): boolean {
      // TODO : Add also user permission checking
      const isCreatable = this.objectTypeMeta ? this.objectTypeMeta.isCreatable : false;
      return isCreatable && !this.isBulkActions;
    },
    getDropdownTitle(): string {
      return this.isBulkActions ? 'Export' : '';
    },
    getDropdownVariant(): string {
      return this.isBulkActions ? 'outline-secondary' : 'outline-secondary fa fa-ellipsis-h';
    },
    getExportTitle(): string {
      return this.isBulkActions ? 'Export as CSV' : 'Export all objects as CSV';
    },
    getDownloadTitle(): string {
      return this.isBulkActions ? 'Download attachments' : 'Download all attachments';
    },
    getSelectedObjectIds(): string[] {
      return this.selectedObjects.map(selected => selected.dbId);
    },
    getExportFilters(): object {
      let filters = {};
      if (!this.isBulkActions && this.activeFilters.length != 0) {
        filters = JSON.parse(composeFilterQuery(this.activeFilters, this.attributeMeta));
      } else if (this.selectedObjects.length > 0) {
        filters = composeFilterByIdsQuery(this.getSelectedObjectIds);
      }
      return filters;
    },
    getAttachmentFilters(): object {
      let filters = {};
      if (!this.isBulkActions && this.activeFilters.length > 0) {
        filters = composeOldListingFilterSyntax(this.activeFilters, this.attributeMeta);
      } else if (this.selectedObjects.length > 0) {
        filters = composeOldFilterByIdsQuery(this.getSelectedObjectIds);
      }
      return filters;
    },
    getSort(): string {
      return composeOldSortSyntax(this.sort);
    },
  },
  methods: {
    ...mapActions({
      showModal: 'modals/show',
      showAlert: 'alerts/show',
    }),
    handleCreateNewObject() {
      if (this.objectType == 'SurveyTemplate') {
        window.location.href = '/survey_builder/new';
        return;
      }

      this.showNewObjectModal();
    },
    async showNewObjectModal() {
      let created = false;

      try {
        await this.showModal({
          type: 'NewObject',
          data: {
            url: `/sor/new_object/${this.objectType}`,
            type: this.objectTypeMeta.nameSingular,
          },
        });

        created = true;
      } catch (e) {
        // User closed the modal
      }

      if (created) {
        this.$emit('refetch');
      }
    },
    async callExportService(
      url: string,
      data: {
        filter: {
          object_name: (...args: any[]) => Promise<any>;
          filters: { expression: any };
          fields: string[];
        }[];
        stripHtmlInExport: boolean;
      }
    ) {
      await postRequest({
        url,
        headers: {
          'Content-Type': 'application/json',
          'X-export-view': 'blocks',
          'X-requested-by': 'gGRC',
          'X-CSRFToken': this.csrfToken,
        },
        data,
        resolver: response => response,
      })
        .then(response => {
          try {
            const filename = filenameFromResponse(response);
            downloadFile(response.data, 'application/zip', filename);
          } catch (e) {
            this.showAlert({
              type: 'error',
              message: 'Oops, something went wrong.',
            });
          }
        })
        .catch(error => {
          /**
           * Necessary since with attachments we should log what happened to
           * ease debugging
           */
          // eslint-disable-next-line no-console
          console.error(error);
          this.showAlert({
            type: 'error',
            message: 'Oops, something went wrong.',
          });
        });
    },
    async exportToCSV() {
      let modalResponse = { stripHtmlInExport: false };
      let totalCount = this.getSelectedObjectIds.length > 0 ? this.getSelectedObjectIds.length : this.totalCount;
      try {
        modalResponse = await this.showModal({
          type: 'ExportConfirmation',
          data: {
            rawObjectCount: totalCount.toString(),
            objectType: this.objectType,
            exportQuery: '',
            actionUrl: '',
            csrfToken: '',
          },
        });
      } catch (e) {
        // User closed the modal
        return;
      }

      const url = '/_service/export_csv';

      const data = {
        filter: [
          {
            object_name: this.objectType,
            fields: columnsToFields(this.columns, this.attributeMeta),
            filters: {
              expression: this.getExportFilters,
            },
          },
        ],
        stripHtmlInExport: modalResponse.stripHtmlInExport,
      };

      this.callExportService(url, data);
    },
    async downloadAttachments(): void {
      const objectIds =
        this.selectedObjects && this.selectedObjects.length > 0
          ? this.getSelectedObjectIds.toString()
          : map(this.data, 'dbId').toString();

      let response = await downloadAttachments(this.csrfToken, this.objectType, objectIds);

      if (response.type != 'success') {
        this.showAlert({
          type: response.type,
          message: response.message,
        });
      }
    },
    downloadFile,
  },
  apollo: {
    objectTypeMeta: {
      query: gql`
        query ObjectTypeMeta($objectType: SearchType!) {
          objectType(name: $objectType) {
            nameSingular
            namePlural
            isCreatable
            isExportable
            isDownloadable
          }
        }
      `,
      variables() {
        return {
          objectType: this.objectType,
        };
      },
      update: data => data.objectType,
    },
    attributeMeta: {
      // TODO: TEMP remove 'backendKey' after changing:
      // - gql filters query to not use old syntax
      // - gql sort query to accept 'by' param in 'name' format
      query: gql`
        query Attributes($objectType: SearchType!, $context: AttributesContext!) {
          objectType(name: $objectType) {
            attrs(context: $context) {
              name
              backendKey # TODO: TEMP
              displayName
            }
          }
        }
      `,
      variables() {
        return {
          objectType: this.objectType,
          context: 'SOR_LISTING',
        };
      },
      update: data => keyBy(data.objectType.attrs, 'name'),
    },
  },
};
